import {
	flexRender,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

type PaginationState = {
	pageIndex: number;
	pageSize: number;
};

export function TanstackTable<T>({
	options,
	paginationOptions,
	className,
}: {
	options: TableOptions<T>;
	paginationOptions?: {
		isPaginated: boolean;
		totalCount: number;
		onPaginationChange?: (state: PaginationState) => void;
	} & PaginationState;
	className?: string;
}) {
	const {
		isPaginated = false,
		pageIndex,
		pageSize,
		totalCount,
		onPaginationChange,
	} = paginationOptions ?? {};
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: pageIndex ?? 0,
		pageSize: pageSize ?? 10,
	});

	// Keep a ref to avoid triggering on initial render
	const isInitialMount = useRef(true);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		onPaginationChange?.(pagination);
	}, [pagination, onPaginationChange]);

	const table = useReactTable({
		...options,
		...(isPaginated
			? {
					manualPagination: true, // server side pagination
					onPaginationChange: setPagination,
					rowCount: totalCount,
					state: {
						...options.state,
						pagination,
					},
				}
			: {}),
	});

	return (
		<div className="w-full space-y-4">
			<div className="relative w-full overflow-auto">
				<table
					data-slot="table"
					className={cn(
						"w-full caption-bottom text-xs border-collapse",
						className,
					)}
				>
					<thead data-slot="table-header" className="border-b border-border">
						{table.getHeaderGroups().map((headerGroup) => (
							<tr
								key={headerGroup.id}
								data-slot="table-row"
								className="border-b border-border transition-colors hover:bg-muted/50"
							>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										data-slot="table-head"
										className="h-10 px-2.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5"
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody data-slot="table-body" className="[&_tr:last-child]:border-0">
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<tr
									key={row.id}
									data-slot="table-row"
									data-state={row.getIsSelected() && "selected"}
									className="border-b border-border transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
								>
									{row.getVisibleCells().map((cell) => (
										<td
											key={cell.id}
											data-slot="table-cell"
											className="p-2.5 align-middle [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0."
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</td>
									))}
								</tr>
							))
						) : (
							<tr data-slot="table-row">
								<td
									data-slot="table-cell"
									colSpan={table.getAllColumns().length}
									className="h-24 text-center text-muted-foreground"
								>
									No results.
								</td>
							</tr>
						)}
					</tbody>
					{table
						.getFooterGroups()
						.some((group) =>
							group.headers.some((header) => header.column.columnDef.footer),
						) && (
						<tfoot
							data-slot="table-footer"
							className="border-t border-border bg-muted/50 font-medium"
						>
							{table.getFooterGroups().map((footerGroup) => (
								<tr key={footerGroup.id} data-slot="table-row">
									{footerGroup.headers.map((header) => (
										<th
											key={header.id}
											data-slot="table-head"
											className="h-10 px-2.5 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0"
										>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.footer,
														header.getContext(),
													)}
										</th>
									))}
								</tr>
							))}
						</tfoot>
					)}
				</table>
			</div>
			{paginationOptions && (
				<div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.firstPage()}
							disabled={!table.getCanPreviousPage()}
						>
							{"<<"}
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage()}
						>
							{"<"}
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage()}
						>
							{">"}
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.lastPage()}
							disabled={!table.getCanNextPage()}
						>
							{">>"}
						</Button>
						<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<span>Page</span>
							<strong className="text-foreground">
								{table.getState().pagination.pageIndex + 1} of{" "}
								{table.getPageCount().toLocaleString()}
							</strong>
						</span>
						<select
							value={table.getState().pagination.pageSize}
							onChange={(e) => {
								table.setPageSize(Number(e.target.value));
							}}
							className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-8 rounded-none border bg-transparent px-2.5 py-1 text-xs transition-colors focus-visible:ring-1 outline-none disabled:cursor-not-allowed disabled:opacity-50"
						>
							{[10, 20, 30, 40, 50].map((pageSize) => (
								<option key={pageSize} value={pageSize}>
									Show {pageSize}
								</option>
							))}
						</select>
					</div>
					<div className="text-xs text-muted-foreground">
						Showing {table.getRowModel().rows.length.toLocaleString()} of{" "}
						{paginationOptions.totalCount.toLocaleString()} rows
					</div>
				</div>
			)}
		</div>
	);
}
